import { isObject } from "helpers/system";
import { NotificationManager } from "react-notifications";
import { licenseExpired, del as logout } from "sources/session";

const odataContextKey = "@odata.context";
const odataValueKey = "value";
const metacountKey = "x-metacount";
const serviceUnavailablePagePath = "/503.html";

function toJson(res: Response, countInHeaders: boolean) {
    const promise = res.json();
    return promise.then((x) => {
        const data = isObject(x) && odataContextKey in x ? x[odataValueKey] : x;
        if (countInHeaders) {
            const count = res.headers.get(metacountKey);
            return { data, count: count ? parseInt(count) : 0 };
        } else {
            return data;
        }
    });
}

export function alert(params: { title?: string; message?: string; type?: keyof typeof NotificationManager }) {
    const options = {
        message: "",
        timeOut: 5000,
        type: "info",
        callback: undefined,
        ...params,
    };
    const { type, message, title, timeOut, callback } = options;
    NotificationManager[type as keyof typeof NotificationManager](message, title, timeOut, callback);
}

export function responseMiddleware(
    res: Response,
    reqOptions: {
        shouldAlert?: boolean;
        alertParams?: { successAlerts: boolean; noContentAlerts: boolean };
        countInHeaders?: boolean;
        withoutErrorAlert?: boolean;
        returnErrorText?: boolean;
        textBody?: boolean;
    },
) {
    const {
        shouldAlert = false,
        alertParams = null,
        countInHeaders = false,
        withoutErrorAlert = false,
        returnErrorText = false,
        textBody = false,
    } = reqOptions;
    const { ok, status } = res;
    const isNoContent = ok && (status === 204 || status === 404);
    const isUnAuthorized = status === 204 || status === 401;
    const isException = status === 400;
    const isNotFound = status === 404;
    const isCustomExpired = status === 498; // custom http status code (license expired)
    const isServiceUnavailable = status === 503;

    if (isServiceUnavailable) {
        return Promise.resolve(serviceUnavailable());
    }

    return (
        isNoContent || isUnAuthorized || isCustomExpired || isException || isNotFound || textBody
            ? res.text()
            : toJson(res, countInHeaders)
    )
        .then((nextRes) => {
            if (ok && [200, 202].includes(status)) {
                return successMiddleware(nextRes, alertParams ? alertParams.successAlerts : shouldAlert);
            } else if (isNoContent) {
                return noContentMiddleware((!!alertParams && alertParams.noContentAlerts) || shouldAlert);
            } else if (isNotFound) {
                return noContentMiddleware(true);
            } else if (isUnAuthorized) {
                return logout();
            } else if (isCustomExpired) {
                return licenseExpired();
            } else if (isException) {
                !withoutErrorAlert && alert({ type: "error", message: nextRes, title: "Ошибка." });
                return returnErrorText ? { errorText: nextRes } : undefined;
            } else {
                return errorMiddleware(nextRes, shouldAlert);
            }
        })
        .catch(innerExceptionHandler);
}

function successMiddleware(res: Response, shouldAlert: boolean) {
    if (shouldAlert) {
        alert({ type: "success", message: "Ваш запрос выполнен успешно.", title: "Успешно!" });
    }
    return res;
}

function noContentMiddleware(shouldAlert: boolean) {
    if (shouldAlert) {
        alert({ type: "info", message: "По вашему запросу ничего не найдено", title: "Не найдено" });
    }
    return false;
}

function serviceUnavailable() {
    if (window.location.pathname !== serviceUnavailablePagePath) {
        const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.replace(`${serviceUnavailablePagePath}?returnUrl=${encodeURIComponent(returnUrl)}`);
    }
    return false;
}

function errorMiddleware(res: { [key: string]: string[] }, shouldAlert: boolean) {
    if (shouldAlert) {
        for (const key in res) {
            alert({ type: "error", message: res[key][0], title: "Ошибка." });
        }
    }
    return false;
}

function innerExceptionHandler() {
    alert({ type: "error", message: "Произошла внутренняя ошибка", title: "Ошибка." });
    return false;
}
